/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";

import { ApolloError } from "@apollo/client";
import {
  useNotificationListQuery,
  NotificationType,
} from "~/generated/graphql";

type Notification = {
  id: string;
  type: NotificationType;
  title: string;
  details: string;
  targetView?: string | null;
  read: boolean;
  createdAt: string;
};

type INotificationProvider = {
  notificationsList: Notification[];
  loading: boolean | undefined;
  error: ApolloError | undefined;
  refetch: () => void;
  unread: number;
};

type IProps = {
  children: React.ReactNode;
};

const POLL_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

function createCtx<ContextType>() {
  const ctx = React.createContext<ContextType | undefined>(undefined);
  ctx.displayName = "NotificationContext";

  function useCtx() {
    const c = React.useContext(ctx);
    if (!c) {
      throw new Error("useCtx must be inside a Provider with a value");
    }
    return c;
  }

  return [useCtx, ctx.Provider] as const;
}

const [useNotificationsAmount, CtxProvider] =
  createCtx<INotificationProvider>();

export { useNotificationsAmount };

export const notificationLimit = 20;

export function NotificationProvider({ children }: IProps) {
  const { data, loading, error, refetch } = useNotificationListQuery({
    variables: { cursor: "", limit: notificationLimit },
    pollInterval: POLL_INTERVAL_MS,
  });

  const notificationsList = data?.notifications?.notifications || [];

  const unread = notificationsList.filter(({ read }) => read === false).length;

  return (
    <CtxProvider value={{ notificationsList, loading, error, refetch, unread }}>
      {children}
    </CtxProvider>
  );
}
